import { apiClient } from '@borradh-workspace/api-client';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * A rendered creative — a video or a graphic — as its endpoint reports it.
 * One shape for the chat row, the ad panel and the media panel, which used to
 * keep three slightly different copies of it.
 */
export interface CreativeAsset {
  id?: string;
  title?: string | null;
  status: string;
  /** Videos only: identifies the saved render engine. */
  schemaVersion?: number | null;
  progress?: number | null;
  processingStage?: string | null;
  blobUrl?: string | null;
  thumbnailUrl?: string | null;
  /** "1:1", "9:16", … — the shape the render will have, for the placeholder. */
  aspectRatio?: string | null;
  errorMessage?: string | null;
  /** Graphics only: the stable GFX-xxxx failure code. */
  errorCode?: string | null;
  /** Graphics render one image per slide; a single graphic has one. */
  outputs?: Array<{
    url: string;
    thumbnailUrl?: string;
    status?: string;
    slideOrder?: number;
  }>;
}

export type CreativeRef = { kind: 'video' | 'graphic'; id: string };

/** 4s — the cadence the graphic and video status cards poll at. */
const POLL_INTERVAL = 4_000;

/**
 * In flight — the states where the worker still owes us pixels.
 *
 * ONE definition. The row, the ad panel and the media panel each had their
 * own, and they disagreed on `draft`: a graphic whose staged edit was
 * rejected sits in `draft` for good, and the surfaces that treated "not
 * finished" as "still rendering" pulsed and polled it forever. `draft` is
 * neither rendering nor failed; it is simply a creative with nothing to show.
 */
export function isCreativeRendering(status: string | undefined): boolean {
  return (
    status === 'queued' || status === 'processing' || status === 'rendering'
  );
}

/** The slides of a graphic that rendered, in order. */
export function creativeSlides(asset: CreativeAsset | undefined) {
  return (asset?.outputs ?? [])
    .filter((o) => o.status !== 'failed')
    .slice()
    .sort((a, b) => (a.slideOrder ?? 0) - (b.slideOrder ?? 0));
}

/**
 * Poll a creative until the worker is done with it.
 *
 * Keyed on `queryKeys.content.asset`, which the status cards poll on too, so
 * one request feeds every surface showing the same render and they can never
 * disagree about whether it has finished. Polling stops at any status that is
 * not in flight — ready, failed, or an idle `draft`.
 *
 * `pending` is "there is a render to wait on and the server has not said it
 * is finished". A failed READ is not pending — that would be a skeleton with
 * no end — so a creative the server cannot return simply has nothing to show.
 */
export function useCreativeAsset(creative: CreativeRef | null) {
  const query = useQuery({
    queryKey: queryKeys.content.asset(
      creative?.kind ?? 'graphic',
      creative?.id ?? ''
    ),
    queryFn: () =>
      apiClient.get<CreativeAsset>(
        `${creative?.kind === 'video' ? 'videos' : 'graphics'}/${creative?.id}`
      ),
    enabled: creative !== null,
    refetchInterval: (q) =>
      q.state.data && isCreativeRendering(q.state.data.status)
        ? POLL_INTERVAL
        : false,
  });
  const asset = query.data;
  const pending =
    creative !== null &&
    !query.isError &&
    (query.isPending || isCreativeRendering(asset?.status));
  return {
    asset,
    pending,
    failed: asset?.status === 'failed',
    /** The first poll has come back and says the worker is still on it. */
    observedRendering: asset !== undefined && isCreativeRendering(asset.status),
    isError: query.isError,
    refetch: query.refetch,
  };
}
